import { useRef } from "react";
import { ExternalLink, RotateCcw } from "lucide-react";
import { FieldError } from "@/components/form/ConfigField";

export const MSG = { req: "Campo obbligatorio", link: "Il testo deve contenere {link}", url: "Inserisci un indirizzo web valido (https://…)", max: (n) => `Massimo ${n} caratteri` };
const TARGETS = [["registrazione", "Registrazione"], ["accedi", "Accedi"], ["assessment", "Assessment (richiede accesso)"], ["profilo", "Profilo (richiede accesso)"], ["home", "Home"], ["privacy", "Informativa privacy"], ["accessibilita", "Dichiarazione di accessibilità"], ["esterno", "Link esterno"]];
const URL_RE = /^https:\/\/[^\s/$.?#][^\s]*\.[^\s]+$/;

export const validateField = (f, v) => {
    if (f.kind === "cta") {
        const e = {};
        if (!v.text?.trim()) e.text = MSG.req;
        else if (v.text.length > f.limit) e.text = MSG.max(f.limit);
        if (v.target === "esterno" && !URL_RE.test((v.url || "").trim())) e.url = MSG.url;
        return Object.keys(e).length ? e : null;
    }
    if (f.required && !v.trim()) return { value: MSG.req };
    if (f.limit && v.length > f.limit) return { value: MSG.max(f.limit) };
    if (f.kind === "email_body" && !v.includes("{link}")) return { value: MSG.link };
    return null;
};

const slug = (k) => k.replace(/[._]/g, "-");

export const Counter = ({ id, len, limit }) => (
    <p id={id} aria-live="polite" className={`text-sm ${len > limit ? "text-edi-error font-bold" : "text-edi-muted"}`} data-testid={`${id}`}>{len}/{limit} caratteri</p>
);

export const FieldActions = ({ f, readOnly, dirty, isDefault, onSave, onRestore, status, pageName }) => (
    <div className="flex flex-wrap items-center gap-3 pt-1">
        {!readOnly && <button type="button" className="btn-primary py-2" disabled={!dirty} onClick={onSave} data-testid={`cms-save-${slug(f.key)}`}>Pubblica</button>}
        {!readOnly && !isDefault && <button type="button" className="btn-secondary py-2" onClick={onRestore} data-testid={`cms-restore-${slug(f.key)}`}><RotateCcw aria-hidden="true" size={16} /> Ripristina originale</button>}
        {f.view && (
            <a href={f.view} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold" data-testid={`cms-view-${slug(f.key)}`}>
                Vedi la pagina<span className="sr-only"> {pageName} (si apre in una nuova scheda)</span><ExternalLink aria-hidden="true" size={14} />
            </a>
        )}
        {status && <p role="status" className={`text-sm font-semibold ${status.kind === "error" ? "text-edi-error" : "text-edi-success"}`} data-testid={`cms-status-${slug(f.key)}`}>{status.text}</p>}
    </div>
);

const Chips = ({ list, onInsert, disabled, idp }) => (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Inserisci un segnaposto nel testo">
        <span className="text-sm text-edi-muted">Segnaposto:</span>
        {list.map((p) => (
            <button key={p} type="button" disabled={disabled} className="rounded-full border border-edi-border bg-edi-tertiary px-3 py-1 text-sm font-mono hover:bg-edi-tertiary-hover" onClick={() => onInsert(p)} data-testid={`${idp}-chip-${p.slice(1, -1)}`}>
                <span className="sr-only">Inserisci </span>{p}
            </button>
        ))}
    </div>
);

export const TextField = ({ f, value, onChange, errors, readOnly, placeholders }) => {
    const id = `cms-${slug(f.key)}`;
    const ref = useRef(null);
    const multi = ["para", "email_body"].includes(f.kind);
    const err = errors?.value;
    const insert = (p) => {
        const el = ref.current;
        const [a, b] = el ? [el.selectionStart, el.selectionEnd] : [value.length, value.length];
        onChange(value.slice(0, a) + p + value.slice(b));
        requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(a + p.length, a + p.length); });
    };
    const Tag = multi ? "textarea" : "input";
    return (
        <div className="space-y-2">
            <label htmlFor={id} className="edi-label">{f.label}{f.required && <span aria-hidden="true"> *</span>}</label>
            {placeholders && !readOnly && <Chips list={placeholders} onInsert={insert} idp={id} />}
            <Tag ref={ref} id={id} className="edi-input" rows={multi ? (f.kind === "email_body" ? 9 : 4) : undefined} value={value} readOnly={readOnly}
                onChange={(e) => onChange(e.target.value)} aria-required={f.required} aria-invalid={err ? "true" : "false"}
                aria-describedby={`${id}-count${err ? ` ${id}-error` : ""}`} data-testid={`${id}-input`} />
            {f.limit && <Counter id={`${id}-count`} len={value.length} limit={f.limit} />}
            <FieldError id={`${id}-error`} error={err} />
        </div>
    );
};

export const CtaField = ({ f, value, onChange, errors, readOnly }) => {
    const id = `cms-${slug(f.key)}`;
    const set = (k, v) => onChange({ ...value, [k]: v });
    const ext = value.target === "esterno";
    return (
        <fieldset className="space-y-3">
            <legend className="edi-label">{f.label}</legend>
            <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                    <label htmlFor={`${id}-text`} className="text-sm font-semibold">Testo *</label>
                    <input id={`${id}-text`} className="edi-input" value={value.text} readOnly={readOnly} onChange={(e) => set("text", e.target.value)} aria-required="true"
                        aria-invalid={errors?.text ? "true" : "false"} aria-describedby={`${id}-text-count${errors?.text ? ` ${id}-text-error` : ""}`} data-testid={`${id}-text-input`} />
                    <Counter id={`${id}-text-count`} len={value.text.length} limit={f.limit} />
                    <FieldError id={`${id}-text-error`} error={errors?.text} />
                </div>
                <div className="space-y-1">
                    <label htmlFor={`${id}-target`} className="text-sm font-semibold">Destinazione</label>
                    <select id={`${id}-target`} className="edi-input" value={value.target} disabled={readOnly} onChange={(e) => set("target", e.target.value)} data-testid={`${id}-target-select`}>
                        {TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>
            </div>
            {ext && (
                <div className="grid gap-3 sm:grid-cols-2 items-start">
                    <div className="space-y-1">
                        <label htmlFor={`${id}-url`} className="text-sm font-semibold">Indirizzo esterno (https://…) *</label>
                        <input id={`${id}-url`} type="url" inputMode="url" className="edi-input" value={value.url} readOnly={readOnly} onChange={(e) => set("url", e.target.value)}
                            aria-invalid={errors?.url ? "true" : "false"} aria-describedby={errors?.url ? `${id}-url-error` : undefined} data-testid={`${id}-url-input`} />
                        <FieldError id={`${id}-url-error`} error={errors?.url} />
                    </div>
                    <label className="flex items-center gap-2 sm:mt-8">
                        <input type="checkbox" className="h-5 w-5 accent-[var(--edi-primary)]" checked={!!value.new_tab} disabled={readOnly} onChange={(e) => set("new_tab", e.target.checked)} data-testid={`${id}-newtab-checkbox`} />
                        Apri in una nuova scheda
                    </label>
                </div>
            )}
        </fieldset>
    );
};
